// \src\pages\home\components\Testimonials.tsx

import { useEffect, useRef, useState } from "react";
import { useAdmin } from "@/context/AdminContext";

interface TestimonialAvatarProps {
  photo: string;
  name: string;
}

function TestimonialAvatar({
  photo,
  name,
}: TestimonialAvatarProps) {
  const [failed, setFailed] = useState(false);

  if (!photo || failed) {
    return (
      <div className="w-12 h-12 rounded-full bg-background-200 border-2 border-background-200 flex items-center justify-center text-foreground-400 shrink-0">
        <i className="ri-user-line text-xl" />
      </div>
    );
  }

  return (
    <img
      src={photo}
      alt={name}
      className="w-12 h-12 rounded-full object-cover object-top border-2 border-background-200 shrink-0"
      onError={() => setFailed(true)}
    />
  );
}

export default function Testimonials() {
  const { data } = useAdmin();
  const testimonials = data.testimonials;
  const carouselRef = useRef<HTMLDivElement>(null);

  const scrollCarousel = (
    direction: "prev" | "next",
  ) => {
    const container = carouselRef.current;

    if (!container) {
      return;
    }

    const maxScroll =
      container.scrollWidth -
      container.clientWidth;

    if (direction === "next") {
      if (
        container.scrollLeft >=
        maxScroll - 10
      ) {
        container.scrollTo({
          left: 0,
          behavior: "smooth",
        });
      } else {
        container.scrollBy({
          left: container.clientWidth,
          behavior: "smooth",
        });
      }

      return;
    }

    if (container.scrollLeft <= 10) {
      container.scrollTo({
        left: maxScroll,
        behavior: "smooth",
      });
    } else {
      container.scrollBy({
        left: -container.clientWidth,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (testimonials.length <= 3) {
      return;
    }

    const intervalId = window.setInterval(
      () => {
        scrollCarousel("next");
      },
      6000,
    );

    return () => {
      window.clearInterval(intervalId);
    };
  }, [testimonials.length]);

  if (testimonials.length === 0) {
    return null;
  }

  return (
    <section className="w-full py-16 px-4 md:px-8 lg:px-12 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block bg-primary-100 text-primary-700 text-xs font-label font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-3">
            Depoimentos
          </span>

          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
            O Que Dizem Nossos Clientes
          </h2>
        </div>

        <div className="relative">
          <div
            ref={carouselRef}
            className="flex overflow-x-auto scroll-smooth snap-x snap-mandatory -mx-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {testimonials.map((t) => (
              <div
                key={t.id}
                className="w-full md:w-1/2 lg:w-1/3 shrink-0 px-3 snap-start"
              >
                <div className="h-full bg-background-50 border border-background-200 rounded-lg p-6">
                  <div className="flex gap-1 mb-4">
                    {Array.from({
                      length: t.stars,
                    }).map((_, i) => (
                      <i
                        key={i}
                        className="ri-star-fill text-amber-400 text-sm"
                      />
                    ))}
                  </div>

                  <p className="text-foreground-700 text-sm leading-relaxed mb-5 italic">
                    &ldquo;{t.text}&rdquo;
                  </p>

                  <div className="flex items-center gap-3">
                    <TestimonialAvatar
                      photo={t.photo}
                      name={t.name}
                    />

                    <div>
                      <p className="font-label font-semibold text-foreground-900 text-sm">
                        {t.name}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {testimonials.length > 3 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                type="button"
                onClick={() =>
                  scrollCarousel("prev")
                }
                className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                aria-label="Depoimentos anteriores"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>

              <span className="text-xs text-foreground-400 font-label">
                {testimonials.length} depoimentos
              </span>

              <button
                type="button"
                onClick={() =>
                  scrollCarousel("next")
                }
                className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                aria-label="Próximos depoimentos"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
